// Cruise Command · Operation Launch · Phase 2 · Payment result pages.
//
// After Stripe Checkout, the traveller lands back on our origin at
// either /payment/success?session_id=… or /payment/cancel.  These are
// unauthenticated views — they poll /api/payments/status/{session_id}
// until the subscription flips to `paid`, then either return the
// traveller to the app (already signed in) or to the marketing site.
import React, { useEffect, useState } from "react";

const backend = process.env.REACT_APP_BACKEND_URL;

export function PaymentSuccess() {
  const [status, setStatus] = useState("pending");
  const [tries, setTries]   = useState(0);
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const r = await fetch(`${backend}/api/payments/status/${encodeURIComponent(sessionId)}`, { credentials: "include" });
        if (cancelled) return;
        if (!r.ok) {
          setStatus(r.status === 503 ? "unavailable" : [401, 403].includes(r.status) ? "signin" : "failed");
          return;
        }
        const d = await r.json();
        if (cancelled) return;
        const next = d.payment_status || "pending";
        if (["failed", "cancelled", "canceled", "expired"].includes(next) || ["failed", "cancelled", "expired"].includes(d.status)) {
          setStatus("failed");
          return;
        }
        setStatus(next === "paid" ? "paid" : tries >= 20 ? "timeout" : "pending");
        if (next !== "paid" && tries < 20) timer = setTimeout(() => setTries((n) => n + 1), 1500);
      } catch { if (!cancelled) setStatus("unavailable"); }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId, tries]);

  return (
    <div className="min-h-screen w-full bg-[#0b132b] text-white flex items-center justify-center px-6"
         data-testid="cc-payment-success">
      <div className="max-w-xl text-center">
        <div className="text-[10px] font-black tracking-[0.28em] uppercase text-emerald-300/80 mb-6">
          Cruise Command · Subscription
        </div>
        {status === "paid" ? (
          <>
            <h1 className="text-4xl md:text-5xl mb-6"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}>
              Welcome aboard.
            </h1>
            <p className="text-white/80 leading-relaxed mb-8">
              Your subscription is active. Volume I · Issue 0001 begins
              composing the moment your first voyage is entered.
            </p>
            <a href="/" data-testid="cc-payment-continue"
               className="inline-block px-6 py-3 rounded-full bg-white text-slate-900 text-[13px] font-semibold tracking-wide hover:bg-emerald-100 transition">
              Enter Cruise Command
            </a>
          </>
        ) : status !== "pending" ? (
          <div role="alert" data-testid="cc-payment-unavailable">
            <h1 data-testid="cc-payment-recovery-heading" className="text-3xl md:text-4xl mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}>
              {status === "unavailable" ? "Billing is currently unavailable." : status === "signin" ? "Sign in to check your subscription." : "We couldn’t confirm your subscription."}
            </h1>
            <p className="text-white/80 leading-relaxed mb-8" data-testid="cc-payment-recovery-message">No successful payment has been confirmed here. You can return to Cruise Command and try again.</p>
            <a href="/" data-testid="cc-payment-recovery-back" className="inline-block px-6 py-3 rounded-full bg-white text-slate-900 text-[13px] font-semibold focus-visible:outline focus-visible:outline-2">Back to Cruise Command</a>
          </div>
        ) : (
          <>
            <h1 className="text-3xl md:text-4xl mb-6"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}>
              Confirming your subscription…
            </h1>
            <p role="status" data-testid="cc-payment-pending" className="text-white/80 leading-relaxed">
              Waiting for payment confirmation. Your subscription is not yet confirmed.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen w-full bg-[#0b132b] text-white flex items-center justify-center px-6"
         data-testid="cc-payment-cancel">
      <div className="max-w-xl text-center">
        <h1 className="text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 500 }}>
          No harm done.
        </h1>
        <p className="text-white/70 leading-relaxed mb-8">
          The subscription wasn't started and nothing was charged. When you're ready, the
          pricing page will be exactly where you left it.
        </p>
        <a href="/pricing" data-testid="cc-payment-back-pricing"
           className="inline-block px-6 py-3 mr-3 rounded-full bg-[#d4a853] text-slate-900 text-[13px] font-semibold tracking-wide">
          Back to pricing
        </a>
        <a href="/" data-testid="cc-payment-back"
           className="inline-block px-6 py-3 rounded-full bg-white text-slate-900 text-[13px] font-semibold tracking-wide">
          Back to Cruise Command
        </a>
      </div>
    </div>
  );
}
